import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useConfig } from "@/contexts/ConfigContext";
import { TrendingUp, Target, Briefcase, Users2, Phone, Video, Mail, CheckSquare, FileText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";

const ACTIVITY_ICONS = { call: Phone, meeting: Video, email: Mail, task: CheckSquare, note: FileText };
const KPI_TONES = {
  blue: "bg-blue-50 border-blue-100 text-blue-700",
  emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
  amber: "bg-amber-50 border-amber-100 text-amber-700",
  violet: "bg-violet-50 border-violet-100 text-violet-700",
};
const BAR_MARGIN = { top: 10, right: 10, bottom: 0, left: -10 };
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const BAR_RADIUS = [6, 6, 0, 0];

const KPI = ({ icon: Icon, label, value, tone = "blue", testid }) => (
  <Card className="p-5 border-slate-200 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
        <div className="mt-2 text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${KPI_TONES[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

const Dashboard = () => {
  const { t } = useLang();
  const cfg = useConfig();
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/stats/overview");
    setStats(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const stageBarData = useMemo(
    () => stats ? (cfg?.stagesByWeight || []).map((s) => ({
      stage: cfg.stageLabel(s.key),
      count: stats.stage_counts[s.key] || 0,
      value: stats.stage_value[s.key] || 0,
      key: s.key,
      color: cfg.stageStyle(s.key).hex,
    })) : [],
    [stats, cfg]
  );

  const winLoss = useMemo(
    () => stats ? [
      { name: t("won_count"), value: stats.won_count, color: "#16A34A" },
      { name: t("lost_count"), value: stats.lost_count, color: "#DC2626" },
    ] : [],
    [stats, t]
  );

  if (!stats) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard-view">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("dashboard")}</h2>
        <p className="text-sm text-slate-500 mt-1">{t("welcome_back")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} label={t("total_pipeline")} value={formatIDR(stats.total_pipeline)} tone="blue" testid="kpi-pipeline" />
        <KPI icon={Target} label={t("weighted_forecast")} value={formatIDR(stats.weighted_forecast)} tone="violet" testid="kpi-forecast" />
        <KPI icon={Briefcase} label={t("active_deals")} value={stats.active_deals} tone="amber" testid="kpi-active" />
        <KPI icon={Users2} label={t("win_rate")} value={`${stats.win_rate}%`} tone="emerald" testid="kpi-winrate" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200 lg:col-span-2 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-slate-900">{t("deals_by_stage")}</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageBarData} margin={BAR_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="stage" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" radius={BAR_RADIUS}>
                  {stageBarData.map((d) => (
                    <Cell key={d.key} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-4">{t("win_loss")}</h3>
          {stats.won_count + stats.lost_count === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-slate-400">{t("no_data")}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={winLoss} innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4}>
                    {winLoss.map((e) => <Cell key={e.name} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="flex justify-center gap-4 mt-2">
            {winLoss.map((w) => (
              <div key={w.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: w.color }}></span>
                {w.name}: <b className="text-slate-900">{w.value}</b>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5 border-slate-200 shadow-sm" data-testid="recent-activities-card">
        <h3 className="text-base font-semibold text-slate-900 mb-4">{t("recent_activities")}</h3>
        {stats.recent_activities.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">{t("empty_activities")}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {stats.recent_activities.map((a) => {
              const Icon = ACTIVITY_ICONS[a.type] || FileText;
              return (
                <li key={a.id} className="flex items-start gap-3 py-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800">{a.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{a.note}</div>
                  </div>
                  <span className="text-[11px] text-slate-400 mono">{new Date(a.created_at).toLocaleDateString()}</span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
